import { get } from 'lodash-es';
import * as prodEnv from '../../../environments/environment.prod';

import {ActivatedRoute, Router} from '@angular/router';
import {AppState, getAppState} from '../../ngrx/app.reducres';
import { MessageTarget, UserLoggedInData, UserLoggedInMessage } from '@cex/cex-messaging-common';
import {Observable, Subject, of as observableOf} from 'rxjs';
import {UpdateGeneralInfoState} from '../../ngrx/app.actions';
import {mergeMap, take} from 'rxjs/operators';

import {AmpEventSourceTypeCommunity} from "../../common/model/amp-event-source-type-community";
import {AmpEventSourceTypeDozi} from "../../common/model/amp-event-source-type-dozi";
import {AmpEventSourceTypeMarketingCloud} from "../../common/model/amp-event-source-type-marketing-cloud";
import {AmpEventSourceTypeRecruiter} from "../../common/model/amp-event-source-type-recruiter";
import {AmpEventSourceTypeZi} from "../../common/model/amp-event-source-type-zi";
import {AnalyticsService, LoginType} from '../../core/services/analytics.service';
import {CommonService} from "../../common/common.service";
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {JwtHelperService} from "@auth0/angular-jwt";
import {LoginConfig} from '../login.config';
import {LoginCookieService} from "../../core/services/login-cookie.service";
import {NotyService} from "../../common/components/noty/noty.service";
import {PlatformModel} from "../../common/model/platform.model";
import {RequestsService} from './requests.service';
import {Store} from '@ngrx/store';
import {ToggleService} from "./toggle.service";
import {UrlService} from "../../core/services/url.service";
import {environment} from '../../../environments/environment';
import {AmpEventSourceTypeAdmin} from "../../common/model/amp-event-source-type-admin";
import {AmpEventSourceTypeEssentials} from "../../common/model/amp-event-source-type-essentials";
import {AmpEventSourceTypeDataPlatform} from "../../common/model/amp-event-source-type-data-platform";
import { NotyMessage } from '../../common/model/noty-message.model';
import {FraudDetectionService} from "./sardine/fraud-detection.service";

export enum TwoFaScreenType {
  SELECT_AUTH_METHOD,
  OKTA_VERIFY_SELECT_DEVICE,
  OKTA_VERIFY_QR_CODE,
  OKTA_VERIFY_CODE,  // not in set up
  GOOGLE_AUTH_SELECT_DEVICE,
  GOOGLE_AUTH_QR_CODE,
  GOOGLE_AUTH_SETUP_VERIFY_CODE,
  GOOGLE_AUTH_CANT_SCAN,
  GOOGLE_AUTH_VERIFY_CODE,
  EMAIL_AUTH_SETUP_VERIFY_CODE,
  EMAIL_AUTH_VERIFY_CODE,
  SMS_AUTH_SETUP,
  SMS_AUTH_VERIFY_CODE
}

/** Biometric setup offered after login. continueLogin() finishes the login (redirect), it's called whether or not setup succeeds. */
export interface BiometricEnrollPrompt {
  ziaccesstoken: string;
  continueLogin: () => void;
}

@Injectable()
export class LoginService {

  constructor(private _router: Router, private _activatedRoute: ActivatedRoute,
              private analyticsService: AnalyticsService, private _http: HttpClient, private _store: Store<AppState>,
              private requestsService: RequestsService, private urlService: UrlService,
              private loginCookieService: LoginCookieService, private notyService: NotyService,
              private toggleService: ToggleService, private commonService: CommonService, private fraudDetectionService: FraudDetectionService) { }

  // Observable string sources
  private _emitChangeSourceForLogin = new Subject<boolean>();
  private _emitChangeSourceForNoty = new Subject<NotyMessage>();
  private _emitChangeSourceForBiometricEnrollPrompt = new Subject<BiometricEnrollPrompt>();

  // Observable string streams
  private _changeEmittedForNoty$ = this.emitChangeSourceForNoty.asObservable();

  isSsoAutoSubmit = false;
  usernameForSso: string;

  private isAppLauncherRecAdmin = false;
  private _isSfna = false;
  private _isOktaErrorQp = false;

  get emitChangeSourceForLogin(): Observable<boolean> {
    return this._emitChangeSourceForLogin;
  }

  get emitChangeSourceForBiometricEnrollPrompt(): Observable<BiometricEnrollPrompt> {
    return this._emitChangeSourceForBiometricEnrollPrompt;
  }

  get emitChangeSourceForNoty() {
    return this._emitChangeSourceForNoty;
  }

  get changeEmittedForNoty$() {
    return this._changeEmittedForNoty$;
  }

  get isOktaErrorQp(): boolean {
    return this._isOktaErrorQp;
  }

  set isOktaErrorQp(isError: boolean) {
    this._isOktaErrorQp = isError;
  }

  /** Check if the url is from Skilljar, and if so add the query parameters to redirect url. */
  static handleSkilljarRedirectUrl(url: string, redirectUrl: string): string {
    const params = UrlService.getSkilljarUrlParams(url);
    if (params) {
      const redirectSearchParams = new URLSearchParams(params);
      redirectUrl += `?${redirectSearchParams.toString()}`;
    }
    return redirectUrl;
  }

  // get redirect url with the multi-platforms info from RO SSO login popup url and store to local storage
  // popup url format: {loginPage}/sso-login-popup-check-cookie-close-window?redirectUrl={loginPage}?isReachout=true&loginAsMultiPlatforms={value}
  static handleRoSsoPopupRedirectUrl(url: string) {
    const urlValues = url.split('?');
    if (urlValues.length === 3) {
      const urlParams = new URLSearchParams(urlValues[2]);
      const appDomain = new URLSearchParams(urlValues[1]).get('redirectUrl');
      const isReachout = urlParams.get('isReachout') === 'true';
      const loginAsMultiPlatforms = urlParams.get('loginAsMultiPlatforms');
      if (appDomain && isReachout && loginAsMultiPlatforms) {
        const roSsoPopupRedirectUrl = `${appDomain}?${urlValues[2]}`;
        console.log(`roSsoPopupRedirectUrl=${roSsoPopupRedirectUrl}`);
        localStorage.setItem('roSsoPopupRedirectUrl', roSsoPopupRedirectUrl);
        if (window.opener) {
          window.opener.postMessage({roSsoPopupRedirectUrl: roSsoPopupRedirectUrl}, location.origin);
        }
      }
    }
  }

  // check if environment is production
  static isProdEnv() {
    return environment.app_domain === prodEnv.environment.app_domain;
  }

  static isAuthorizedSamlSsoRedirectUrl(redirectUrl: string) {
    return redirectUrl.startsWith(environment.oktaLoginUrl) &&
      (redirectUrl.includes('skilljar') || redirectUrl.includes('salesforce'));
  }

  static getOktaTokenStorage() {
    return JSON.parse(localStorage.getItem('okta-token-storage'));
  }

  // there will be code and state query params after successful Okta widget login
  static isSuccessfulLoginQp(paramStr: string): boolean {
    return paramStr.includes('code=') && paramStr.includes('state=');
  }

  // check if login page is in iframe
  static isInIframe(): boolean {
    return window.self !== window.top;
  }

  /** Check if login page is in webview for enterprise mobile app. */
  static isEnterpriseMobileLogin(): boolean {
    const pathname = window.location.pathname;
    return pathname.includes('webview') || localStorage.getItem(LoginConfig.WEBVIEW_LS) === 'true';
  }

  /** Check if in ZI Chat mobile app login. */
  static isGtmcLogin(): boolean {
    return localStorage.getItem(LoginConfig.GTMC_LOGIN_LS) === 'true';
  }

  /** If in webview for enterprise mobile or GTMC login, send message to mobile app with the necessary query params. Returns true for mobile login. */
  handleMobileWebviewFlow(ziid: string, zisession: string, ziaccesstoken: string, loginType: LoginType): boolean {
    if (LoginService.isEnterpriseMobileLogin() || LoginService.isGtmcLogin()) {
      localStorage.removeItem(LoginConfig.WEBVIEW_LS);
      localStorage.removeItem(LoginConfig.GTMC_LOGIN_LS);
      LoginService.reactNativeWebViewPostMessage({
        ziid,
        zisession,
        ziaccesstoken,
        loginType
      });
      return true;
    }
    return false;
  }

  // postMessage to mobile app
  static reactNativeWebViewPostMessage(message: any) {
    try {
      // this postMessage accepts a string only
      window["ReactNativeWebView"].postMessage(JSON.stringify(message));
    } catch (err) {
      console.error(err);
    }
  }

  // retrieve username from mobile app
  getUsernameFromMobile(): string {
    return this.loginCookieService.getCookie('username');
  }

  /** Check if the mobile SSO login was initiated. */
  isMobileAutoSsoLoginInitiated(): boolean {
    return localStorage.getItem('mobileAutoSsoLoginInitiated') === 'true';
  }

  // check if user have both SalesOS and AdminPortal
  static isSosAdmin(availablePlatformsNames: Array<string>): boolean {
    const lowerCasePlatforms = availablePlatformsNames.map(platform => platform.toLowerCase());
    return lowerCasePlatforms.includes('dozi') && lowerCasePlatforms.includes('admin');
  }

  static getDecodedZiAccessToken(ziaccesstoken: string): any {
    try {
      return new JwtHelperService().decodeToken(ziaccesstoken);
    } catch (error) {
      console.error('Error decoding ziaccesstoken', error);
      return {};
    }
  }

  /**
   * @parseErrorFromServer
   *
   * This method is responsible for parsing the error from the server and response with a proper object.
   * **/
  parseErrorFromServer(err, defaultErrorMessage) {
    const defaultErrorObj = {message: {errorMessage: defaultErrorMessage}, code: err.status || 500};
    const errorObj = get(err, 'error', defaultErrorObj);
    const finalError = Object.keys(errorObj).length ? errorObj : defaultErrorObj;

    return finalError;
  }

  /**
   * @parseBeResponse
   *
   * This method is responsible for parsing the new response from the new Login BE.
   * @output { data, anura }
   * **/
  parseBeResponse(resp) {
    const { data, anura } = resp;
    return {data, anura};
  }

  sendLoginAmpEvent(used2FA): Promise<[object, object]> {
    let appState;
    this._store.select(getAppState).pipe(take(1)).subscribe((state) => appState = state);
    const { user } = appState;
    const platform = this.getCurrentPlatform();
    const zoomRoles = get(user, 'zoomRoles', []);
    const machineIdForAnalytics =  this.loginCookieService.getCookie('machineId') || "null";

    const generalUserData = {
      Admin: get(user, 'isAdmin', false),
      CompanyID: get(user, 'zoomCompanyId', 0),
      RemainingUserCredits: get(user, 'credits', -1),
      MachineID: machineIdForAnalytics,
      Used2FA: used2FA
    };

    const redirectUrl = this.urlService.getLoginFinalRedirectUrl();
    const infoFromRedirectUrl = this.urlService.getRedirectUrlInfo(redirectUrl);
    const ampEventLoginTo = this.getAmpEventLoginTo(platform, infoFromRedirectUrl, zoomRoles);
    //
    const ampEventProperties = Object.assign({}, LoginConfig.INITIAL_LOGGED_IN_EVENT_PARAMS, {LoginTo: ampEventLoginTo}, generalUserData);

    return this.analyticsService.sendAmplitudeEvent('LoggedIn', ampEventProperties, true);
  }

  /**
   *
   * **/
  commonActionsAfterLoginRelatedApiFailure(err) {
    if (get(err, 'error.blockScript') && get(err, 'error.appId')) {
      return this.pixelBlockScriptHandler(err.error);
    }
  }

  /**
   * @setCookiesAndRedirectAfterLogin
   *
   * This method is responsible for:
   *    1. Setting up the routing Cookie
   *    2. Setting up the session cookies
   *    3. Redirect to the relevant url by the following priority:
   *      A. University page, let's redirect him to University.
   *      B. "redirect" Query Param in the URL, let's redirect him to that.
   *      C. Redirect url provided from the backend.
   * **/
  async setCookiesAndRedirectAfterLogin(resp) {
    console.log('getLoginType', this.analyticsService.getLoginType());
    this.routingCookieHandling();
    //Send login details to fraud detection service, but don't block login flow if it fails
    try {
      console.log('login successful - sending details to fraud detection service', resp["userId"]);
      const currentLoginType = this.analyticsService.getLoginType();
      if (currentLoginType === LoginType.SSO) {
        console.log("sessionKey not found in cookies, creating new sessionKey for fraud detection service");
        try {
          const newKey = await this.fraudDetectionService.createSession(resp["userId"]);
          console.log("sessionKey from sso", newKey);
          this.fraudDetectionService.sendLoginDetails(newKey, resp["ziaccesstoken"], currentLoginType);
        } catch (err) {
          console.log("sessionKey not available", err);
        }
      } else {
        console.log("Skipping fraud detection - LoginType is not SSO:", currentLoginType);
      }
    } catch (err) {
      console.warn('Failed to create session for fraud detection:', err);
    }

    // Getting App State
    this._store.select(getAppState).pipe(
      take(1))
      .subscribe((appState) => {
        const userState = get(appState, 'user');
        const authState = get(appState, 'auth');
        const generalInfoState = get(appState, 'generalInfo');

        this.setLoginCookies(resp, userState);

        // Get Final Redirect URL
        const finalRedirectUrl = this.urlService.getLoginFinalRedirectUrl();
        const infoFromRedirectUrl = this.urlService.getRedirectUrlInfo(finalRedirectUrl);

        // University Login
        const universityLoginToken = authState.universityLoginToken;
        if (universityLoginToken) {
          return this.buildUniversityLoginAndRedirect(universityLoginToken);
        }

        /**
         * Check if it's a contributor.
         *    A. If it is - connect him to platform and redirect with relevant URL params
         *    B. If not - perform a regular redirection
         * **/
        if (generalInfoState.isContributor) {
          return this.contributorConnectAndLogin(generalInfoState, finalRedirectUrl, userState, authState);
        }
        /**
         * Toggle Flow
         *
         * Restrict Extensions
         *
         * **/
        if (userState.toggle) {
          return this.handleToggledUserAfterSuccessfulLogin(userState);
        }
        // Regular redirect
        window.location.href = finalRedirectUrl;
      });
  }

  /**
   * This method's purpose is to keep the current details we have on URL:
   *    1. Query Params
   *    2. Root Route Path
   * **/
  navigateWithUrlInfo(route) {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    let pathFromRoot = this._router.url.split('?')[0];
    pathFromRoot = pathFromRoot.replace('/logout', '/');
    const finalRoute = pathFromRoot + route;
    this._router.navigate([finalRoute], {queryParams});
  }

  /**
   * This method responds with the machineId for 2FA.
   *
   * Considering 2 cases:
   *    A. In case there is already machineId in the cookies --> return that.
   *    B. In case there isn't --> Calculate, save in the cookies and return that.
   *
   * @returns machineId
   * **/
  twoFaMachineIdHandling(isOktaLogin = false) {
    const machineIdCookieName = isOktaLogin ? LoginConfig.MACHINE_ID_STRING_CONVENTION_OKTA : LoginConfig.MACHINE_ID_STRING_CONVENTION;
    if (!this.loginCookieService.getCookie(machineIdCookieName)) {
      const machineId = this.calcMachineId();
      this.loginCookieService.setCookie(machineIdCookieName, machineId, 365, '/', environment.cookies_domain);
      return machineId;
    }
    return this.loginCookieService.getCookie(machineIdCookieName);
  }

  /**
   * @is_support_err
   *
   * This method is responsible to identify weather the error we got from the backend
   * Is a support error message or not.
   * **/
  isSupportErr(err) {
    const errToCheck = get(err, 'errorMessage', '');
    if (errToCheck) {
      return errToCheck.indexOf('contact support') > -1;
    }
    return false;
  }

  /**
   * Extract the relevant home page of the user by providing the response from User Details endpoint
   * **/
  extractHomePageByUserDetailsResp(userDetailsResp) {
    // If there is a "redirect" query param provided via URL and it's valid --> redirect to it
    const redirectQp = this.urlService.getInfoFromUrl().redirectQp;
    if (redirectQp && this.urlService.isUrlAllowedForRedirect(redirectQp)) {
      return redirectQp;
    }

    let homePage = get(userDetailsResp, 'appUrls.webAppUrl');
    /**
     * Modify Home Page
     * **/
    if (this.urlService.isUrlAllowedForRedirect(homePage)) {
      /**
       * subscriber
       * **/
      if (homePage.includes('subscriber')) {

        // If "ziversion" cookie === '2.0' --> redirect to Enterprise
        if (this.loginCookieService.getCookie('ziversion') === '2.0') {
          console.log(`user, redirect to :: '${environment.zi_url_enterprise}' for ziid :: '${this.loginCookieService.getCookie('ziid')}' :: page :: '${homePage}'`);
          return environment.zi_url_enterprise;
        }
        // Replacing http to https for subscriber domains ONLY!
        homePage = homePage.replace('http://', 'https://');
      }
      /**
       * Dev / Staging Cases
       * **/
      if (!environment.production) {
        // Change only Grow Domains and not 1.0 domains to Grow Staging URL
        homePage = homePage.replace('ce.zoominfo', 'grow-staging.zoominfo').replace('go.zoominfo', 'grow-staging.zoominfo');

        // Change only DOZI Domain to DOZI Staging Domain
        homePage = homePage.replace('app.zoominfo', 'dozi-staging.zoominfo');

        // Change only 1.0 Domain and not Grow domains to Subscriber Staging URL
        homePage = homePage.replace('subscriber.zoominfo.com', 'subscriber.cloud-2.ge-staging.zoominfo.com/nextgen');
      }
      return homePage;
    }
    return environment.website_domain;
  }

  /**
   * This part is temporary for controlling the traffic on app engine for the release of ng5 from ce to go
   * **/
  routingCookieHandling(expirationDate?) {
    expirationDate = expirationDate || LoginConfig.DEFAULT_EXPIRATION_DATE_FOR_ROUTING_COOKIE;
    let routingCookie = this.loginCookieService.getCookie('GOOGAPPUID');
    if (!routingCookie) {
      routingCookie = (1 + Math.floor(Math.random() * 998)).toString();
      this.loginCookieService.setCookie('GOOGAPPUID', routingCookie, expirationDate, '/', environment.cookies_domain);
    }
  }

  /**
   * This method is responsible to build the university login endpoint and redirect to that.
   * It's purpose is to do all the relevant logic in one place.
   * **/
  buildUniversityLoginAndRedirect(universityLoginToken) {
    const nextQp = this._activatedRoute.snapshot.queryParams.next || '/';
    const finalRedirectUrl = `${LoginConfig.ZOOM_UNIVERSITY_DOMAIN}${LoginConfig.SKILLJAR_LOGIN_ENDPOINT}?token=${universityLoginToken}&next=${encodeURIComponent(nextQp)}`;
    window.location.href = finalRedirectUrl;
  }

  /**
   * This method is responsible for posting messages to RO
   * Using try{} catch(){} for safety
   * **/
  postMessageToReachoutApp(message) {
    try {
      // Post a message to "ReachOut" Chrome Extension's window
      window.parent.postMessage({name: message, target: 'EXTENSION'}, '*');
      console.log(`Posted a message to RO :: ${message}`);
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * Send messages to RO after successful login
   */
  loginToReachOutAndSendToPlatform(platform) {
    this.postmessageToReachoutAppWithUserAuthData();
    this.postMessageToReachoutAppWithAppMode(platform);
  }

  /**
   * This method is responsible for posting messages to RO with the correct mode
   * **/
  private postMessageToReachoutAppWithAppMode(anura) {
    const extensionMode = this.getExtensionMode(anura);
    if (!environment.production) {
      console.log(`posting message to reachout app: ${extensionMode}`);
    }
    window.parent.postMessage({ name: 'setAppMode', target: 'IFRAME', mode: extensionMode }, '*');
  }

  /**
   * This method is responsible for posting message to RO with user's auth data to support RO's navbar content and feature flagging
   */
  private postmessageToReachoutAppWithUserAuthData(): void {
    if (!environment.production) {
      console.log(`posting UserLoggedIn to reachout app`);
    }

    const ziId = this.loginCookieService.getCookie('ziid');
    const ziSession = this.loginCookieService.getCookie('zisession');
    const ziAccessToken = this.loginCookieService.getCookie('ziaccesstoken');
    const zoomUserId = this.loginCookieService.getCookie('userId');
    const isCopilotUser = this.loginCookieService.getCookie(LoginConfig.COPILOT_USER_COOKIE_NAME) === 'true';
    const authData: UserLoggedInData = { zoomUserId, ziId, ziSession, ziAccessToken, isCopilotUser };
    window.parent.postMessage({ name: UserLoggedInMessage.MESSAGE_TYPE, target: MessageTarget.REACHOUT_EXTENSION_APP, data: authData }, '*');  // NOSONAR
  }

  /**
   * this function returns the extension mode by the platform - currently supports only recruiter and DOZI
   * **/
  getExtensionMode(anura) {
    switch (anura) {
      case PlatformModel.RECRUITER: return 'RECRUITER';
      case PlatformModel.DOZI: default: return 'DOZI';
    }
  }

  /**
   * this function returns reachout url by the relevant anura
   * **/
  getReachoutUrlByAnura(anura) {
    switch (anura) {
      case PlatformModel.RECRUITER: return environment.recruiter_url_reachout;
      case PlatformModel.DOZI: default: return environment.dozi_url_reachout;
    }
  }

  /**
   * this function returns relevant anura by the reachout url
   * **/
  getAnuraByReachoutUrl(url: string) {
    return url === environment.recruiter_url_reachout ? PlatformModel.RECRUITER : PlatformModel.DOZI;
  }

  /**
   * This method is responsible for posting messages to SF app
   * Using try{} catch(){} for safety
   * **/
  postMessageToSFApp(options) {
    try {
      const isLziSfna = this.isLziSfna();
      //
      if (isLziSfna) {
        const sfPostObject = {
          'x-ziid': options.ziid,
          'x-zisession': options.zisession,
          'user': options.userId,
          'session-token': options.parseSessionToken,
          'zoom_role': options.zoom_role,
          'name': options.name,
          'email': options.email,
          'target': 'SFDC'
        };
        // Post a message to "SalesForce" Chrome Extension's window
        window.parent.postMessage(sfPostObject, '*');
        console.log(`Posted a message to SF :: ${JSON.stringify(sfPostObject)}`);
      }
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * This method is responsible for the base logic of getting the current platform the user belongs to
   * first checks if the url is the lzi login url, and if not:
   * @Rules:
   *  1. Exists on state --> That's the last indication we made on the state so !! Take from State !!
   *
   *  2. ZI:
   *    isRedirectedFromZi || isContributor || isUniversityPage
   *
   *  3. DOZI:
   *    isRedirectedFromDozi
   * **/
  getCurrentPlatform(): string {
    let appState;
    const infoFromUrl = this.urlService.getInfoFromUrl();
    this._store.select(getAppState).subscribe((state) => appState = state);
    const { generalInfo } = appState;
    const { isContributor, isUniversityPage, platform } = generalInfo;
    const { isRedirectedFromZi, isRedirectedFromDozi, isRedirectedFromRecruiter, isRedirectedFromMarketingCloud, isRedirectedFromZIBackOffice, isRedirectedFromAdmin, isRedirectedFromZiLite, isRedirectedFromEssentials, isRedirectedFromDataPlatform, isRedirectedFromGtmCopilot, isRedirectedFromCombinedPlatform } = infoFromUrl;
    const isLziLoginVersion = this.commonService.isLziLoginVersion();
    //
    if (isLziLoginVersion) { // if it's lzi login page - we want the platform to be always zi
      return PlatformModel.ZI;
    }
    if (platform) { return platform; }
    if (isRedirectedFromZi || isContributor || isUniversityPage || isRedirectedFromZIBackOffice) { return PlatformModel.ZI; }
    if (isRedirectedFromMarketingCloud) { return PlatformModel.MARKETING_CLOUD; }
    if (isRedirectedFromDozi) { return PlatformModel.DOZI; }
    if (isRedirectedFromRecruiter) { return PlatformModel.RECRUITER; }
    if (isRedirectedFromAdmin) { return PlatformModel.ADMIN; }
    if (isRedirectedFromZiLite) { return PlatformModel.ZI_LITE; }
    if (isRedirectedFromEssentials) { return PlatformModel.ESSENTIALS; }
    if (isRedirectedFromDataPlatform) { return PlatformModel.DATA_PLATFORM; }
    if (isRedirectedFromGtmCopilot) { return PlatformModel.GTM_COPILOT; }
    // TODO: Update once the verbiage is confirmed for the platforms
    if (isRedirectedFromCombinedPlatform) { return PlatformModel.GTM_COPILOT; }
    // Respond with empty string by default
    return '';
  }

  /**
   * Private Methods
   * **/
  private calcMachineId() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  }

  /**
   * Set the relevant cookies after successful login
   * **/
  setLoginCookies(resp, userState?) {
    this.loginCookieService.setCookie('ziid', resp['ziid'], 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('zisession', resp['zisession'], 90, '/', environment.cookies_domain);
    if (resp['ziaccesstoken']) {
      this.loginCookieService.setCookie('ziaccesstoken', resp['ziaccesstoken'], 90, '/', environment.cookies_domain);
      // Set cookie for cache busting
      const helper = new JwtHelperService();
      const decodedAccessToken = helper.decodeToken(resp['ziaccesstoken']);
      this.loginCookieService.setCookie(LoginConfig.LATEST_TOKEN_EXPIRATION_COOKIE_NAME, String(decodedAccessToken.exp), 90, '/', environment.cookies_domain);

      const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: 'ziaccesstoken', value: resp['ziaccesstoken'], target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
        APP_IFRAME_CONTAINER.postMessage({ key: LoginConfig.LATEST_TOKEN_EXPIRATION_COOKIE_NAME, value: decodedAccessToken.exp, target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    }
    if (resp['autossologin']) {
      this.loginCookieService.setCookie('autossologin', resp['autossologin'], 365 * 2, '/', environment.cookies_domain); // 2 years
      const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: 'autossologin', value: resp['autossologin'], target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    }
    const redirectUrl = this.urlService.getLoginFinalRedirectUrl();
    const infoFromRedirectUrl = this.urlService.getRedirectUrlInfo(redirectUrl);
    console.log(infoFromRedirectUrl.isRedirectedFromSFNA);
    if (resp['ssoredirecturl']) {
      this.loginCookieService.setCookie('ssoredirecturl', decodeURIComponent(resp['ssoredirecturl']), new Date(new Date().getTime() +  3 * 60 * 1000), '/', environment.cookies_domain);
      const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: 'ssoredirecturl', value: resp['ssoredirecturl'], target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    }
    this.loginCookieService.setCookie('parseSessionToken', resp.parseSessionToken, 365, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('userId', resp.userId, 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('userEmail', resp.email, 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('email', resp.email, 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('name', resp.name, 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('firstname', resp.firstname, 90, '/', environment.cookies_domain);
    this.loginCookieService.setCookie('userZoomCompanyId', resp.zoomCompanyId || userState?.zoomCompanyId, 90, '/', environment.cookies_domain);
    //
    this.loginCookieService.setCookie('analyticsId', resp.userId, 90, '/', environment.cookies_domain);

    // Set session storage
    const tokensInSessionStorage = [{'key': 'ziid', 'value': resp['ziid']}, {'key': 'zisession', 'value': resp['zisession']},
      {'key': 'parseSessionToken', 'value': resp.parseSessionToken}, {'key': 'userId', 'value': resp.userId},
      {'key': 'userEmail', 'value': resp.email}, {'key': 'email', 'value': resp.email}, {'key': 'name', 'value': resp.name},
      {'key': 'firstname', 'value': resp.firstname}, {'key': 'userZoomCompanyId', 'value': resp.zoomCompanyId}, {'key': 'analyticsId', 'value': resp.userId}];

    const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
    tokensInSessionStorage.forEach(tokenItem => {
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: tokenItem.key, value: tokenItem.value, target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    });
  }

  /**
   * Clear the login cookies
   *
   * 1. ziid
   * 2. zisession
   * 3. parseSessionToken
   * 4. userId
   * 5. analyticsId
   * **/
  clearLoginCookies() {
    this.loginCookieService.deleteCookie('ziid', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('zisession', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('ziaccesstoken', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('parseSessionToken', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('userId', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('userEmail', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('email', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('name', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie('firstname', '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie(LoginConfig.LATEST_TOKEN_EXPIRATION_COOKIE_NAME, '/', environment.cookies_domain);
    this.loginCookieService.deleteCookie(LoginConfig.LOOP_COOKIE_NAME, '/', environment.cookies_domain);
    // The old login set that cookie too, TODO - need to ask if this cookie is needed:
    this.loginCookieService.deleteCookie('analyticsId', '/', environment.cookies_domain);
    const tokenNames = ['ziid', 'zisession', 'ziaccesstoken', 'parseSessionToken', 'userId', 'userEmail', 'email', 'name', 'firstname', LoginConfig.LOOP_COOKIE_NAME, 'analyticsId', LoginConfig.LATEST_TOKEN_EXPIRATION_COOKIE_NAME];
    const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
    tokenNames.forEach(tokenName => {
      sessionStorage.removeItem(tokenName);
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: tokenName, target: "LOGIN_MESSAGE", action: "Delete" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    });
  }

  clearLoopCookie() {
    this.loginCookieService.deleteCookie(LoginConfig.LOOP_COOKIE_NAME, '/', environment.cookies_domain);
  }

  /**
   * set the loop cookies
   * @param clearLoginCookies
   */
  setLoopCookie(loginLoopCookie) {

    // create cookie with ttl of 45 seconds
    const loginLoopCounter = loginLoopCookie ? parseInt(loginLoopCookie) + 1 : 1;
    const loopDetectorCookieExpired = new Date(new Date().getTime() + (LoginConfig.LOOP_COOKIE_EXPIRATION));

    // set the cookie
    this.loginCookieService.setCookie(
      LoginConfig.LOOP_COOKIE_NAME,
      loginLoopCounter.toString(),
      loopDetectorCookieExpired
    );
    const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
    if (APP_IFRAME_CONTAINER) {
      APP_IFRAME_CONTAINER.postMessage({ key: LoginConfig.LOOP_COOKIE_NAME, value: loginLoopCounter.toString(), target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
    }
  }

  /**
   * This method is responsible for connecting to one of the contributor platforms:
   *    1. ICE
   *    2. Swapper
   * **/
  /* istanbul ignore next */
  private contributorConnectAndLogin(generalInfoState, finalRedirectUrl, userState, authState) {
    const token = get(generalInfoState, 'parsedContributorParam.token');
    const clientId = get(generalInfoState, 'parsedContributorParam.client_id');
    const platform = get(generalInfoState, 'platform');
    const ziid = this.loginCookieService.getCookie('ziid');
    const zisession = this.loginCookieService.getCookie('zisession');

    const userId = get(userState, '_id');
    const parseSessionToken = get(authState, 'parseSessionToken');
    const name = get(userState, 'name');

    return observableOf(null).pipe(
      take(1),
      mergeMap(() => {
        if (userId) {
          if (token) { return this.requestsService.connectUserWithToken(ziid, zisession, userId, parseSessionToken, token, platform); }
          if (clientId) { return this.requestsService.connectUserWithClientId(ziid, zisession, userId, parseSessionToken, clientId, platform); }
        }
        return observableOf(null);
      }),)
      .subscribe(
        (resp) => {
          const {anura, data} = this.parseBeResponse(resp);
          if (!get(data, 'credits')) {
            return window.location.href = finalRedirectUrl;
          }
          const paramsObj = {credits: data['credits'], name};
          let popUpParamsObj = JSON.stringify(Object.assign({}, generalInfoState.parsedContributorParam, paramsObj));
          popUpParamsObj = btoa(popUpParamsObj);
          window.location.href = finalRedirectUrl + LoginConfig.GROW_PROSPECTING_PAGE + popUpParamsObj;
        },
        (err) => {
          window.location.href = finalRedirectUrl;
        }
      );
  }

  /**
   * This method is responsible for the PIXEL block script handling
   *
   * !! Set the scripts under the app_domain path (login.zoominfo.com / login-staging.zoominfo.com)
   * For times i run the app locally infront of Staging / Prod !!
   *
   * **/
  private pixelBlockScriptHandler(err) {
    window['_pxAppId'] = err.appId;
    window['_pxJsClientSrc'] = environment.app_domain + err.jsClientSrc;
    window['_pxFirstPartyEnabled'] = err.firstPartyEnabled;
    window['_pxVid'] = err.vid;
    window['_pxUuid'] = err.uuid;
    window['_pxHostUrl'] = environment.app_domain + err.hostUrl;
    window['_pxreCaptchaTheme'] = 'light';
    window['_pxOnCaptchaSuccess'] = () => {
      console.log('_pxOnCaptchaSuccess got triggered !!!');
      this._store.dispatch(new UpdateGeneralInfoState({pixelCaptchaNeeded: false, pixelCaptchaRefId: '', pixelInitScript: ''}));
    };
    // Updating state with pixel data
    this._store.dispatch(new UpdateGeneralInfoState({
      pixelCaptchaNeeded: true,
      pixelInitScript: environment.app_domain + err.blockScript,
      pixelCaptchaRefId: this.getRefId()
    }));
  }

  /**
   * This method is responsible to extract the Reference ID for the PX Captcha
   * **/
  private getRefId() {
    let pxUuid = window['_pxUuid'];
    //
    if (!pxUuid) {
      const url = window.location.href;
      const regex = new RegExp('[?&]uuid(=([^&#]*)|&|#|$)');
      const results = regex.exec(url);
      pxUuid = get(results, 'length') > 2 ? results[2] : '';
      pxUuid = decodeURIComponent(pxUuid.replace(/\+/g, ' '));
    }
    //
    return pxUuid;
  }

  /**
   * This method is responsible to provide the relevant Amplitude event source
   * **/
  /* istanbul ignore next */
  private getAmpEventLoginTo(platform, infoFromRedirectUrl, zoomRoles) {
    //
    switch (platform) {
      case PlatformModel.DOZI: {
        if (infoFromRedirectUrl.isRedirectedFromReachOut) {
          return AmpEventSourceTypeDozi.REACHOUT;
        }
        else if (infoFromRedirectUrl.isRedirectedFromSFNA) {
          return AmpEventSourceTypeDozi.SFNA;
        }
        else if (infoFromRedirectUrl.isRedirectedFromMDNA) {
          return AmpEventSourceTypeDozi.MDNA;
        }
        else {
          return AmpEventSourceTypeDozi.DOZI;
        }
      }
      case PlatformModel.ZI_LITE:
        return AmpEventSourceTypeDozi.ZI_LITE;
      case PlatformModel.ZI: {
        if (infoFromRedirectUrl.isRedirectedFromReachOut) {
          return this.getReachOutEnterprise(zoomRoles);
        }
        else if (infoFromRedirectUrl.isRedirectedFromZi) {
          return this.getZIEnterprise(zoomRoles);
        }
        else {
          return AmpEventSourceTypeZi.OTHER;
        }
      }
      case PlatformModel.RECRUITER: {
        if (infoFromRedirectUrl.isRedirectedFromReachOut) {
          return AmpEventSourceTypeRecruiter.REACHOUT;
        }
        return AmpEventSourceTypeRecruiter.RECRUITER;
      }
      case PlatformModel.MARKETING_CLOUD: {
        return AmpEventSourceTypeMarketingCloud.MARKETING;
      }
      case PlatformModel.COMMUNITY: {
        return AmpEventSourceTypeCommunity.COMMUNITY;
      }
      case PlatformModel.ESSENTIALS: {
        return AmpEventSourceTypeEssentials.ESSENTIALS;
      }
      case PlatformModel.DATA_PLATFORM: {
        return AmpEventSourceTypeDataPlatform.DATA_PLATFORM;
      }
      case PlatformModel.ADMIN: {
        return AmpEventSourceTypeAdmin.ADMIN;
      }
    }
    return '';
  }


  /**
   * This method is responsible to check if the user that about to login, is an active zi\dozi user.
   *  this is for back compatibility - this cookie userd to represent a logged in user without considering the platform
   * **/
  isActiveUser() {
    return !!this.loginCookieService.getCookie(LoginConfig.ACTIVE_USER_COOKIE_NAME);
  }

  /**
   * This method is responsible to check if the user that about to login, is an active zi user.
   * **/
  isZiActiveUser() {
    return !!this.loginCookieService.getCookie(LoginConfig.ACTIVE_ZI_USER_COOKIE_NAME);
  }

  /**
   * This method is responsible to check if the user that about to login, is an active dozi user.
   * **/
  isDoziActiveUser() {
    return !!this.loginCookieService.getCookie(LoginConfig.ACTIVE_DOZI_USER_COOKIE_NAME);
  }

  /**
   * This method is responsible to check if the user that about to login, is an active recruiter user.
   * **/
  isRecruiterActiveUser() {
    return !!this.loginCookieService.getCookie(LoginConfig.ACTIVE_RECRUITER_USER_COOKIE_NAME);
  }

  /**
   * This method is responsible to check if the user that about to login, is an active recruiter user.
   * **/
  isMarketingCloudActiveUser() {
    return !!this.loginCookieService.getCookie(LoginConfig.ACTIVE_MARKETING_CLOUD_USER_COOKIE_NAME);
  }

  /**
   * This method is responsible to return the cookie name for logged in user by platform
   * **/
  getUserCookieName(platform) {
    switch (platform) {
      case PlatformModel.RECRUITER: return LoginConfig.ACTIVE_RECRUITER_USER_COOKIE_NAME;
      case PlatformModel.MARKETING_CLOUD: return LoginConfig.ACTIVE_MARKETING_CLOUD_USER_COOKIE_NAME;
      case PlatformModel.ZI: return LoginConfig.ACTIVE_ZI_USER_COOKIE_NAME;
      default: return LoginConfig.ACTIVE_DOZI_USER_COOKIE_NAME;
    }
  }

  /**
   * This method is responsible to check whether the user is an enterpise user or not
   * **/
  isUserEnterprise(zoomRoles = []) {
    return zoomRoles.includes('GROW_ENTERPRISE') || zoomRoles.includes('GROW_BETA') || zoomRoles.includes('ENTERPRISE');
  }

  /**
   * This method is responsible to remember active users.
   * Saving a cookie that indicates that the user is active.
   * Valid for 3 months.
   * **/
  rememberActiveUser(resp) {
    const platform = this.getCurrentPlatform();
    const isZICommunityUser = platform === PlatformModel.ZI && !this.isUserEnterprise(resp.zoom_role);
    //
    const dontRememberCases = [isZICommunityUser];
    // If it's one of don't remember cases:
    //    1. Ignore
    //    2. Remove existing cookie
    if (dontRememberCases.some(item => item)) {
      this.loginCookieService.deleteCookie(LoginConfig.ACTIVE_ZI_USER_COOKIE_NAME, '/', environment.cookies_domain);
      this.loginCookieService.deleteCookie(LoginConfig.ACTIVE_DOZI_USER_COOKIE_NAME, '/', environment.cookies_domain);
      this.loginCookieService.deleteCookie(LoginConfig.ACTIVE_RECRUITER_USER_COOKIE_NAME, '/', environment.cookies_domain);
      this.loginCookieService.deleteCookie(LoginConfig.ACTIVE_MARKETING_CLOUD_USER_COOKIE_NAME, '/', environment.cookies_domain);
      return;
    }
    const userCookieName = this.getUserCookieName(platform);
    this.loginCookieService.setCookie(userCookieName, resp.email, 90, '/', environment.cookies_domain);
  }

  /**
   * This method is responsible to handle users that needs to be toggled.
   * Get the toggle status from the state and redirect to toggle page
   * **/
  handleToggledUserAfterSuccessfulLogin(userState: any = {}) {
    const { toggleFrom, toggleTo } = userState;
    const redirectUrl = this.urlService.getLoginFinalRedirectUrl();
    const infoFromRedirectUrl = this.urlService.getRedirectUrlInfo(redirectUrl);
    //
    let toggleParams = {from: toggleFrom, to: toggleTo, flow: LoginConfig.TOGGLE_FLOWS.LOGIN};
    const additionalToggleParams = {toUrl: '', fromUrl: '', redirectQp: '', forceMovingToAppOnFailure: false};
    // Extension:
    //    1. toUrl - in case of success toggle flow, redirect to redirect query param
    //    2. In case of a failure - want to stay on login page with a notification of the error
    if (infoFromRedirectUrl.isRedirectedFromExtension) {
      additionalToggleParams.toUrl = redirectUrl;
    }
      // Not Extension:
      //    1. toUrl - in case of success toggle flow, redirect to redirect query param
    //    2. forceMovingToAppOnFailure - Force moving to the relevant app on failure
    else {
      additionalToggleParams.toUrl = redirectUrl;
      additionalToggleParams.forceMovingToAppOnFailure = true;
    }
    toggleParams = Object.assign({}, toggleParams, additionalToggleParams);
    //
    this._store.dispatch(new UpdateGeneralInfoState({isLoading: true}));
    return this.toggleService.toggleAndRedirect(toggleParams);
  }

  checkExistenceOfErrorQpAndHandle() {
    const { errorQp } = this.urlService.getInfoFromUrl();
    if (errorQp) {
      try {
        const decoded_error_query_param = atob(errorQp);
        const errorQueryParamObj = JSON.parse(decoded_error_query_param);
        console.log(errorQueryParamObj);
      } catch (err) {
        console.log(err);
      }
    }
  }

  isExtension() {
    return window.location !== window.parent.location;
  }

  isInsideAnIframe() {
    return window.location !== window.parent.location;
  }

  isLziSfna() {
    const isInsideAnIframe = this.isInsideAnIframe();
    const redirectQp = this.urlService.getInfoFromUrl().redirectQp;
    //
    return !!(isInsideAnIframe && !redirectQp);

  }

  /**
   * checking if the input is a number and retuning it, otherwise returning default number
   * **/
  validateAndGetNumber(number, defaultNumber: number = 500) {
    try {
      if (number) {
        return !isNaN(Number(number)) ? Number(number) : defaultNumber;
      } else {
        return defaultNumber;
      }
    } catch (e) {
      return defaultNumber;
    }
  }

  getReachOutEnterprise(zoomRoles) {
    return this.isUserEnterprise(zoomRoles) ? AmpEventSourceTypeZi.REACHOUT : AmpEventSourceTypeZi.CE_REACHOUT;
  }

  getZIEnterprise(zoomRoles) {
    return this.isUserEnterprise(zoomRoles) ? AmpEventSourceTypeZi.GO : AmpEventSourceTypeZi.CE;
  }

  setRedirectUrlCookie() {
    console.log(`get ssoredirecturl from url=${this._router.url}`);
    const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
    const clientRedirectUrl = queryParamMap.get('redirect');
    let ssoredirecturl = this.urlService.isUrlAllowedForRedirect(clientRedirectUrl) ? clientRedirectUrl : '';
    ssoredirecturl = LoginService.handleSkilljarRedirectUrl(location.href, ssoredirecturl);
    console.log(`get ssoredirecturl=${ssoredirecturl} from redirect queryParam`);
    const redirectUrl = this.urlService.getLoginFinalRedirectUrl();
    const infoFromRedirectUrl = this.urlService.getRedirectUrlInfo(redirectUrl);
    console.log(infoFromRedirectUrl.isRedirectedFromSFNA);

    // Use redirect param for local testing, otherwise desktop login redirect will use URL from the same environment.
    if (this.isDesktopLogin() && !this.urlService.getInfoFromUrl().isRedirectedFromDevApp) {
      ssoredirecturl = environment.combined_platform_url;
      console.log(`set ssoredirecturl=${ssoredirecturl} for desktop login`);
    }

    /* istanbul ignore next */
    if (ssoredirecturl) {
      // set encoded ssoredirecturl into cookie ssoredirecturl
      this.loginCookieService.setCookie('ssoredirecturl', encodeURIComponent(ssoredirecturl), new Date(new Date().getTime() +  3 * 60 * 1000), '/', environment.cookies_domain);
    } else {
      this.loginCookieService.deleteCookie('ssoredirecturl', '/', environment.cookies_domain);
      const APP_IFRAME_CONTAINER = get(document.getElementById("app_iframe_container"), 'contentWindow');
      if (APP_IFRAME_CONTAINER) {
        APP_IFRAME_CONTAINER.postMessage({ key: 'ssoredirecturl', value: encodeURIComponent(ssoredirecturl), target: "LOGIN_MESSAGE", action: "Set" }, environment.dozi_url_enterprise + LoginConfig.SESSION_STORAGE_POST_URL);
      }
    }
  }

  /**
   * checking if should show Engage Welcome Banner by:
   * 1. isLDForEngageWelcomeBannerIsON
   * we want to show Engage Welcome Banner if the relevant flag is turned on
   * **/
   shouldShowEngageWelcomeBanner(): boolean {
     //REFACTOR: remove this method
     return false;
  }

  // Service message commands
  emitNotyChange(change: NotyMessage) {
    this._emitChangeSourceForNoty.next(change);
  }

  emitOktaLoginSuccess(success: boolean) {
    this._emitChangeSourceForLogin.next(success);
  }

  emitBiometricEnrollPrompt(prompt: BiometricEnrollPrompt) {
    this._emitChangeSourceForBiometricEnrollPrompt.next(prompt);
  }

  /** A screen must be listening to show the biometric setup prompt, otherwise the login would never continue. */
  hasBiometricEnrollPromptListener(): boolean {
    return this._emitChangeSourceForBiometricEnrollPrompt.observed;
  }

  // show or hide lastpass icons for username and password inputs depending on the show flag
  toggleLoginFormLastPassIconDisplay(show: boolean) {
    const usernameLPIcon = document.getElementById('__lpform_okta-signin-username_icon') as HTMLImageElement;
    const pwLPIcon = document.getElementById('__lpform_okta-signin-password_icon') as HTMLImageElement;

    if (usernameLPIcon && pwLPIcon) {
      usernameLPIcon.style.display = show ? 'initial' : 'none';
      pwLPIcon.style.display = show ? 'initial' : 'none';
    }
  }

  // determine platform and return reachout url of it
  convertToReachoutUrl(url: string) {
    if (url.indexOf('recruiter') !== -1)
      return this.getReachoutUrlByAnura(PlatformModel.RECRUITER);
    return this.getReachoutUrlByAnura(PlatformModel.DOZI);
  }

  /* istanbul ignore next */
  // update redirect url if matching app and feature flag is on
  updateRedirectUrlOnFF(redirectUrl: string) {
    if (redirectUrl === environment.dozi_url_community && LoginConfig.IS_DOZI_LITE_ENABLED) {
          redirectUrl = environment.zi_lite_url;
    }
    return redirectUrl;
  }

  // set reachoutsso cookie for SSO login if in RO
  handleSsoCookieForRO(isReachout: boolean) {
    if (isReachout) {
      const myDate = new Date();
      this.loginCookieService.setCookie('reachoutsso', 'true', myDate.setMinutes(myDate.getMinutes() + 3), '/', environment.cookies_domain);
      console.log(`Set reachoutsso=${this.loginCookieService.getCookie('reachoutsso')}`);
    }
  }

  // popup window from SSO login will send a redirect url, store to local storage if it not stored already
  listenForSsoPopupRedirectUrl() {
    /* istanbul ignore next */
    window.addEventListener('message', (e) => {
      // check sender for security
      if (e.origin !== window.origin) {
        console.warn('listenForSsoPopupRedirectUrl - message from', e.origin);
        return;
      }
      console.log('listenForSsoPopupRedirectUrl - message received', e.data);
      if (!localStorage.getItem('popupredirecturl') && e.data.popupredirecturl) {
        localStorage.setItem('popupredirecturl', e.data.popupredirecturl);
      }
      if (!localStorage.getItem('roSsoPopupRedirectUrl') && e.data.roSsoPopupRedirectUrl) {
        localStorage.setItem('roSsoPopupRedirectUrl', e.data.roSsoPopupRedirectUrl);
      }
    });
  }

  // determine if user will have app launcher recommending Admin Portal
  checkForAppLauncherRecAdmin(): void {
    //REFACTOR: remove method
    this.isAppLauncherRecAdmin = LoginConfig.SHOULD_RECOMMEND_ADMIN;
  }

  getIsAppLauncherRecAdmin(): boolean {
    //REFACTOR: remove method
    return this.isAppLauncherRecAdmin;
  }

  checkForMaintenanceBannerFlag(): boolean {
    return LoginConfig.SHOW_MAINTENANCE_BANNER;
  }

  // Each flow has its own authorize endpoint in login BE. The IdP redirect url the
  // backend returned is not used: LBE starts the flow itself and resolves the IdP
  // from the idpid cookie.
  getMobileSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/mob/authorize`;
  }

  getReachoutSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/ro/authorize`;
  }

  getSfnaSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/sfna/authorize`;
  }

  getPartnerSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/partner/authorize`;
  }

  getCopilotWSSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/cws/authorize`;
  }

  getGtmcSsoRedirectUrl(redirectUrl: string): string {
    return `${this.getLbeDomain()}/sso/oidc/gtmc/authorize`;
  }

  /** Default web SSO authorize endpoint, used when no flow-specific endpoint applies. */
  getSsoRedirectUrl(): string {
    return `${this.getLbeDomain()}/sso/oidc/authorize`;
  }

  private getLbeDomain(): string {
    return environment.is_new_login_backend ? environment.new_login_backend_path : environment.login_backend_domain;
  }

  /** Filter for platforms that are available in the app launcher. */
  static getRealPlatformNames(platformNames: string[]): string[] {
    return platformNames.filter(name => name !== PlatformModel.AAT && name !== PlatformModel.CHORUS);
  }

  /** Check if the user only has AAT license. */
  static hasOnlyAAT(ziPlatforms: string[]): boolean {
    return ziPlatforms.length === 1 && ziPlatforms[0].toLowerCase() === PlatformModel.AAT;
  }

  /** Check if the user only has Chorus license. */
  static hasOnlyChorus(ziPlatforms: string[]): boolean {
    return ziPlatforms.length === 1 && ziPlatforms[0].toLowerCase() === PlatformModel.CHORUS;
  }

  public set isSfna(flag: boolean) {
    this._isSfna = flag;
  }

  public get isSfna(): boolean {
    return this._isSfna;
  }

  async storeRefreshTokens(ziaccesstoken: string, refreshtoken: string) {
    const STORE_REFRESH_TOKEN_ENDPOINT = `${environment.sm_refresh_storage_url}`;

    await this.requestsService.sendStoreRefreshTokenRequest(STORE_REFRESH_TOKEN_ENDPOINT, ziaccesstoken, refreshtoken)
      .catch(err => console.error(`Call to ${STORE_REFRESH_TOKEN_ENDPOINT} failed with ${err}`));
  }

  /**
   * Show error for blocked user.
   */
  handleBlockedUser() {
    console.error('Issue with account. Aborting login.');
    // reload the page to handle query param and reset Okta widget
    this.setUrlParameterAndReload({
      issueWithAccount: true
    });
  }

  /**
   * Redirect to error page for a user with no access to a platform.
   */
  handleNoAccessUser() {
    window.location.href = `${environment.app_domain}/${LoginConfig.NO_ACCESS_LANDING_PAGE_PATH}`;
  }

  // add parameter to url and reload to reset page
  setUrlParameterAndReload(queryParams) {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',  // preserve the existing query params in the route
      skipLocationChange: false
    }).then(() => {
      window.location.reload();
    });
  }

  /** Set cookie for SSO if in the partner login page. */
  checkForPartnerLoginSSO(): boolean {
    const partnerLoginInfo = localStorage.getItem(LoginConfig.PARTNER_LOGIN_INFO_LS);
    if (partnerLoginInfo) {
      // set cookie expiration to 1 hour
      this.loginCookieService.setCookie(LoginConfig.PARTNER_LOGIN_COOKIE, partnerLoginInfo, new Date(new Date().getTime() +  60 * 60 * 1000), '/', environment.cookies_domain);
      return true;
    }
    return false;
  }

  /** Check if desktop login from URL or user agent. */
  isDesktopLogin(): boolean {
    const { isDesktopLogin } = this.urlService.getInfoFromUrl();
    const userAgent = window.navigator.userAgent;
    return isDesktopLogin || (userAgent != null && userAgent.includes('gtm-one') && userAgent.includes('Electron'));
  }

  /** Listen for OneTrust cookie banner to load then unhide it. */
  listenForCookieBannerAndShow() {
    let oneTrustContainerObserver: MutationObserver;
    const isMobileAfterLogin = (LoginService.isEnterpriseMobileLogin() || LoginService.isGtmcLogin()) && LoginService.isSuccessfulLoginQp(location.search);

    try {
      oneTrustContainerObserver = new MutationObserver(mutationRecords => {
        this.showOneTrustBanner(isMobileAfterLogin, oneTrustContainerObserver);
      });
      const targetNode = document.body;
      oneTrustContainerObserver.observe(targetNode, { childList: true, subtree: true });
      // check for container outside MutationObserver in case it already loaded
      this.showOneTrustBanner(isMobileAfterLogin, oneTrustContainerObserver);
    } catch (err) {
      if (oneTrustContainerObserver && oneTrustContainerObserver.disconnect) {
        oneTrustContainerObserver.disconnect();
      }
    }
  }

  showOneTrustBanner(isMobileAfterLogin: boolean, observer: MutationObserver) {
    const oneTrustContainer = document.getElementById('onetrust-consent-sdk');

    // don't show container for mobile after login to prevent possible flickering on Android
    if (oneTrustContainer && !isMobileAfterLogin) {
      oneTrustContainer.style.display = 'initial';
      observer.disconnect();
    }
  }
}
